import { computed, getCurrentInstance, nextTick, ref } from 'vue'
import { channelConfig } from '@/utils/cable'

/**
 * 聊天功能 Hook
 * 使用 actioncable-vue 实现实时聊天
 */
export function useChat(options = {}) {
  const { userId, onMessageReceived, onUserStatusChanged, onTypingStatusChanged } = options
  const instance = getCurrentInstance()
  const cable = instance?.proxy?.$cable || instance?.appContext?.config?.globalProperties?.$cable

  // 连接状态
  const isConnected = ref(false)
  const isConnecting = ref(false)
  const connectionError = ref(null)

  // 聊天状态
  const chatMessages = ref([])
  const typingUsers = ref(new Set())
  const onlineUsers = ref(new Set())

  // 当前会话
  const currentConversation = ref(null)
  const currentChannelName = ref(null)

  // 计算属性
  const connectionStatus = computed(() => {
    if (isConnecting.value) return 'connecting'
    if (isConnected.value) return 'connected'
    if (connectionError.value) return 'error'
    return 'disconnected'
  })

  const typingUsersArray = computed(() => Array.from(typingUsers.value))
  const onlineUsersArray = computed(() => Array.from(onlineUsers.value))

  /**
   * 连接到 ActionCable
   */
  const connect = async () => {
    if (!cable) {
      console.error('ActionCable 未初始化')
      return false
    }

    try {
      isConnecting.value = true
      connectionError.value = null

      // 使用 actioncable-vue 的连接方法
      if (cable && typeof cable.connect === 'function') {
        cable.connect()
        isConnected.value = true
        isConnecting.value = false
        console.log('ActionCable 连接成功')
      } else if (cable.connection && typeof cable.connection.open === 'function') {
        cable.connection.open()
        isConnected.value = true
        isConnecting.value = false
        console.log('ActionCable 连接成功')
      } else {
        // 假设已经连接
        isConnected.value = true
        isConnecting.value = false
        console.log('ActionCable 已连接')
      }

      return true
    } catch (error) {
      isConnecting.value = false
      connectionError.value = error.message
      console.error('ActionCable 连接失败:', error)
      return false
    }
  }

  /**
   * 断开连接
   */
  const disconnect = () => {
    if (cable) {
      if (typeof cable.disconnect === 'function') {
        cable.disconnect()
      } else if (cable.connection && typeof cable.connection.disconnect === 'function') {
        cable.connection.disconnect()
      }
      isConnected.value = false
      isConnecting.value = false
      currentChannelName.value = null
    }
  }

  /**
   * 订阅聊天频道
   */
  const subscribeToChat = (conversationId, userId) => {
    if (!cable) {
      console.error('ActionCable 未连接')
      return null
    }

    const channelName = `ChatChannel_${conversationId}`
    const channelParams = {
      channel: channelConfig.chatChannel,
      conversation_id: conversationId,
      user_id: userId
    }

    try {
      // 使用 actioncable-vue 的订阅方法
      cable.subscribe(channelParams, channelName, {
        connected() {
          console.log('聊天频道连接成功:', conversationId)
        },
        disconnected() {
          console.log('聊天频道断开:', conversationId)
        },
        received(data) {
          handleReceivedMessage(data)
        },
        rejected() {
          console.error('聊天频道订阅被拒绝:', conversationId)
        }
      })

      currentConversation.value = conversationId
      currentChannelName.value = channelName

      console.log('订阅聊天频道:', conversationId)

      return channelName
    } catch (error) {
      console.error('订阅频道失败:', error)
      return null
    }
  }

  /**
   * 处理接收到的消息
   * 这个方法需要在组件中通过 channels 选项配置
   */
  const handleReceivedMessage = (data) => {
    const { type, payload } = data

    switch (type) {
      case 'message': {
        // 转换后端消息格式为前端需要的格式
        const message = {
          id: payload.id,
          content: payload.content,
          sender: payload.sender,
          sender_id: payload.sender_id,
          avatar: payload.avatar,
          timestamp: payload.timestamp,
          isSelf: payload.sender_id === userId, // 根据发送者ID判断是否为自己的消息
          conversation_id: payload.conversation_id
        }
        chatMessages.value.push(message)
        onMessageReceived?.(message)
        break
      }

      case 'typing':
        if (payload.typing) {
          typingUsers.value.add(payload.user_id)
        } else {
          typingUsers.value.delete(payload.user_id)
        }
        onTypingStatusChanged?.(payload)
        break

      case 'user_online':
        onlineUsers.value.add(payload.user_id)
        onUserStatusChanged?.(payload)
        break

      case 'user_offline':
        onlineUsers.value.delete(payload.user_id)
        onUserStatusChanged?.(payload)
        break

      case 'message_read':
        // 处理消息已读状态
        console.log('消息已读:', payload)
        break

      default:
        console.log('未知消息类型:', type, payload)
    }
  }

  /**
   * 发送聊天消息
   */
  const sendChatMessage = (content, conversationId, messageType = 'text') => {
    if (!cable || !conversationId || !content?.trim()) {
      console.error('参数无效：ActionCable未连接、会话ID为空或消息内容为空')
      return false
    }

    const channelName = currentChannelName.value || `ChatChannel_${conversationId}`

    // 检查频道是否已订阅
    if (!currentChannelName.value) {
      console.error('频道未订阅，无法发送消息:', channelName)
      return false
    }

    const messageData = {
      action: 'send_message',
      content,
      message_type: messageType,
      conversation_id: conversationId,
      timestamp: new Date().toISOString()
    }

    try {
      cable.perform(channelName, messageData)
      return true
    } catch (error) {
      console.error('发送消息失败:', error)
      return false
    }
  }

  /**
   * 发送输入状态
   */
  const sendTypingStatus = (conversationId, isTyping) => {
    if (!cable || !conversationId) {
      console.warn('ActionCable 未连接或会话ID为空，无法发送输入状态')
      return
    }

    const channelName = currentChannelName.value || `ChatChannel_${conversationId}`

    // 检查频道是否已订阅
    if (!currentChannelName.value) {
      console.warn('频道未订阅，无法发送输入状态:', channelName)
      return
    }

    try {
      cable.perform(channelName, {
        action: 'typing',
        typing: isTyping,
        conversation_id: conversationId
      })
    } catch (error) {
      console.error('发送输入状态失败:', error)
    }
  }

  /**
   * 标记消息为已读
   */
  const markMessageAsRead = (messageId, conversationId) => {
    if (!cable || !conversationId) {
      console.warn('ActionCable 未连接或会话ID为空，无法标记消息为已读')
      return
    }

    const channelName = currentChannelName.value || `ChatChannel_${conversationId}`

    // 检查频道是否已订阅
    if (!currentChannelName.value) {
      console.warn('频道未订阅，无法标记消息为已读:', channelName)
      return
    }

    try {
      cable.perform(channelName, {
        action: 'mark_read',
        message_id: messageId,
        conversation_id: conversationId
      })
    } catch (error) {
      console.error('标记消息已读失败:', error)
    }
  }

  /**
   * 清空聊天消息
   */
  const clearMessages = () => {
    chatMessages.value = []
  }

  /**
   * 取消订阅当前频道
   */
  const unsubscribeFromChat = () => {
    if (cable && currentChannelName.value) {
      cable.unsubscribe(currentChannelName.value)
      currentChannelName.value = null
      currentConversation.value = null
    }
  }

  /**
   * 获取频道配置对象
   * 用于在 Vue 组件的 channels 选项中使用
   */
  const getChannelConfig = (conversationId, _userId) => {
    return {
      [channelConfig.chatChannel]: {
        connected() {
          console.log('聊天频道连接成功:', conversationId)
          isConnected.value = true
        },

        disconnected() {
          console.log('聊天频道断开:', conversationId)
          isConnected.value = false
        },

        received(data) {
          handleReceivedMessage(data)
        },

        rejected() {
          console.error('聊天频道订阅被拒绝:', conversationId)
          connectionError.value = '频道订阅被拒绝'
        }
      }
    }
  }

  /**
   * 标记消息为已读
   */
  const markAsRead = (messageId, conversationId) => {
    markMessageAsRead(messageId, conversationId)
  }

  /**
   * 加载会话消息
   */
  const loadConversationMessages = async (conversationId) => {
    // 这里可以调用 API 加载历史消息
    console.log('加载会话消息:', conversationId)
    return []
  }

  /**
   * 滚动到底部
   */
  const scrollToBottom = () => {
    nextTick(() => {
      const chatContainer = document.querySelector('.chat-messages')
      if (chatContainer) {
        chatContainer.scrollTop = chatContainer.scrollHeight
      }
    })
  }

  return {
    // 状态
    isConnected,
    isConnecting,
    connectionError,
    connectionStatus,
    chatMessages,
    typingUsers: typingUsersArray,
    onlineUsers: onlineUsersArray,
    currentConversation,
    currentChannelName,

    // 方法
    connect,
    disconnect,
    subscribeToChat,
    sendMessage: sendChatMessage,
    sendTypingStatus,
    markAsRead,
    loadConversationMessages,
    scrollToBottom,
    clearMessages,
    unsubscribeFromChat,
    handleReceivedMessage,
    getChannelConfig
  }
}
